import React, { useState, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import LazyImage from '../../common/LazyImage';
import ImageModal from '../../common/ImageModal';
import ShareModal from '../../common/ShareModal';
import { linkifyTextToJSX } from '../../../utils/textUtils';
import { parseVideoUrl, getVideoThumbnail, fetchVimeoThumbnail, detectVideoInText } from '../../../utils/videoUtils';
import { downloadActivityICS } from '../../../utils/calendarUtils';

// SVG badge icons — solid black stroke, no emoji
const DocIcon = () => (
  <svg className="badge-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 1h5.5L13 4.5V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z"/>
    <polyline points="9.5 1 9.5 5 13 5"/>
  </svg>
);

const VideoIcon = () => (
  <svg className="badge-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="3" width="10" height="10" rx="1"/>
    <path d="M11 6.5l4-2v7l-4-2"/>
  </svg>
);

const LinkIcon = () => (
  <svg className="badge-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6.5 9.5a3.5 3.5 0 0 0 5 0l1.5-1.5a3.5 3.5 0 0 0-5-5L7 4"/>
    <path d="M9.5 6.5a3.5 3.5 0 0 0-5 0L3 8a3.5 3.5 0 0 0 5 5l1-1"/>
  </svg>
);

const ActivityItem = memo(({ activity, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showFullReflection, setShowFullReflection] = useState(false);
  const [videoThumbnail, setVideoThumbnail] = useState(null);
  const [videoLink, setVideoLink] = useState(null);

  useEffect(() => {
    const resolveVideo = async () => {
      // Priority 1: dedicated videoUrl field
      let parsed = activity.videoUrl ? parseVideoUrl(activity.videoUrl) : null;

      // Priority 2: auto-detect from reflection text
      if (!parsed && activity.reflection) {
        parsed = detectVideoInText(activity.reflection);
      }

      if (!parsed) return;

      setVideoLink(parsed.url);

      if (parsed.platform === 'youtube') {
        setVideoThumbnail(getVideoThumbnail('youtube', parsed.videoId));
      } else if (parsed.platform === 'vimeo') {
        const thumb = await fetchVimeoThumbnail(parsed.url);
        if (thumb) setVideoThumbnail(thumb);
      }
    };

    resolveVideo();
  }, [activity.videoUrl, activity.reflection]);

  const formatDate = (timestamp) => {
    if (timestamp && timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString();
    }
    return 'N/A';
  };

  const formatDateTime = (timestamp) => {
    if (timestamp && timestamp.toDate) {
      const date = timestamp.toDate();
      return date.toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
    return 'N/A';
  };

  // Helper function to render text with clickable links
  const renderTextWithLinks = (text) => {
    if (!text) return null;

    const parts = linkifyTextToJSX(text);

    return parts.map((part, index) => {
      if (typeof part === 'string') {
        return <span key={`text-${index}`}>{part}</span>;
      } else if (part.type === 'link') {
        return (
          <a
            key={part.key}
            href={part.href}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#FFD100',
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {part.text}
          </a>
        );
      }
      return null;
    });
  };

  // Safely check for images with fallback
  const hasImage = Boolean(
    (activity.imageUrl && activity.imageUrl.trim() !== '') ||
    (activity.thumbnailUrl && activity.thumbnailUrl.trim() !== '')
  );
  const hasReflection = Boolean(activity.reflection && activity.reflection.trim() !== '');
  const hasDocument = Boolean(activity.documentUrl && activity.documentName);
  const hasVideo = Boolean(videoThumbnail && videoLink);
  const hasDocumentLinks = Boolean(activity.documentLinks && activity.documentLinks.trim());

  // Count document links
  const documentLinkCount = hasDocumentLinks
    ? activity.documentLinks.trim().split('\n').filter(l => l.trim()).length
    : 0;

  // Use thumbnail for list view, full-size for expanded view with null safety
  const thumbnailUrl = activity.thumbnailUrl || activity.imageUrl || '';
  const fullSizeUrl = activity.imageUrl || activity.thumbnailUrl || '';

  // Truncate reflection to first 2 lines (approximately 100 characters)
  const truncateReflection = (text) => {
    if (!text) return '';
    const maxLength = 100;
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const needsReadMore = hasReflection && activity.reflection.length > 100;

  // Check if any attachments exist (for badge row)
  const hasAnyAttachments = hasDocument || hasVideo || hasDocumentLinks;

  return (
    <>
      <div className={`activity-item ${isExpanded ? 'expanded' : ''}`}>
        {/* Row 1: Edit/Delete links - top right */}
        {(onEdit || onDelete) && (
          <div className="activity-manage-links">
            {onEdit && (
              <button
                className="activity-edit-link"
                onClick={() => onEdit(activity)}
              >
                {t('common.edit')}
              </button>
            )}
            {onDelete && (
              <button
                className="activity-delete-link"
                onClick={() => onDelete(activity)}
              >
                {t('common.delete')}
              </button>
            )}
          </div>
        )}

        {/* Row 2: Title */}
        <h3 className="activity-card-title">{activity.title}</h3>

        {/* Row 3: Date + Calendar + Attachment Badges */}
        <div className="activity-meta-row">
          <span className="activity-meta-date">{formatDate(activity.timestamp)}</span>
          <button
            className="calendar-export-link"
            onClick={(e) => {
              e.stopPropagation();
              downloadActivityICS(activity);
            }}
          >
            {t('progress.addToCalendar')}
          </button>

          {hasAnyAttachments && (
            <>
              <span className="meta-separator" />
              <div className="attachment-badges">
                {hasDocument && (
                  <span className="attachment-badge">
                    <DocIcon />
                    1 Doc
                  </span>
                )}
                {hasVideo && (
                  <span className="attachment-badge">
                    <VideoIcon />
                    1 Video
                  </span>
                )}
                {documentLinkCount > 0 && (
                  <span className="attachment-badge">
                    <LinkIcon />
                    {documentLinkCount} {documentLinkCount === 1 ? 'Link' : 'Links'}
                  </span>
                )}
              </div>
            </>
          )}
        </div>

        {/* Row 4: Cause icon/photo (left) + Reflection (right) */}
        <div className="activity-card-body">
          <div className="cause-icon-col">
            {hasImage ? (
              <div
                className="cause-photo-box"
                onClick={() => setShowModal(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setShowModal(true);
                  }
                }}
              >
                <LazyImage
                  src={thumbnailUrl}
                  alt={activity.title}
                  placeholder="📷"
                />
              </div>
            ) : (
              <div className="cause-icon-box">
                <img
                  src={activity.cause?.icon || '/icons/icon_activity.png'}
                  alt={activity.cause?.title || 'Activity'}
                  style={{ width: '28px', height: '28px', objectFit: 'contain' }}
                />
              </div>
            )}
            <span className="cause-tag">{activity.cause.title}</span>
          </div>

          {hasReflection && (
            <p className="activity-reflection-preview">
              {!isExpanded && !showFullReflection
                ? renderTextWithLinks(truncateReflection(activity.reflection))
                : !isExpanded
                  ? renderTextWithLinks(activity.reflection)
                  : renderTextWithLinks(truncateReflection(activity.reflection))
              }
            </p>
          )}
        </div>

        {/* Expanded content */}
        {isExpanded && (
          <div className="activity-expanded-content">
            {/* Full-size photo */}
            {hasImage && (
              <div className="activity-full-image">
                <img src={fullSizeUrl} alt={activity.title} />
              </div>
            )}

            {/* Full reflection */}
            {hasReflection && (
              <div className="activity-full-reflection">
                <h4>{t('progress.reflection')}</h4>
                <p>{renderTextWithLinks(activity.reflection)}</p>
              </div>
            )}

            {/* Attached document */}
            {hasDocument && (
              <div style={{ marginTop: '12px', marginBottom: '12px' }}>
                <h4 style={{
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: '#666',
                  marginBottom: '8px'
                }}>
                  {t('progress.attachedDocument')}
                </h4>
                <a
                  href={activity.documentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="expanded-doc-link"
                >
                  <DocIcon />
                  <span className="expanded-doc-name">
                    {activity.documentName}
                  </span>
                  <span className="expanded-doc-ext">↗</span>
                </a>
              </div>
            )}

            {/* Document Links */}
            {hasDocumentLinks && (
              <div style={{ marginTop: '12px', marginBottom: '12px' }}>
                <h4 style={{
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: '#666',
                  marginBottom: '8px'
                }}>
                  {t('progress.documentLinks')}
                </h4>
                {activity.documentLinks.trim().split('\n').filter(l => l.trim()).map((link, i) => (
                  <a
                    key={i}
                    href={link.trim().startsWith('http') ? link.trim() : `https://${link.trim()}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="expanded-url-link"
                  >
                    <LinkIcon />
                    <span>{link.trim()}</span>
                    <span className="expanded-doc-ext">↗</span>
                  </a>
                ))}
              </div>
            )}

            {/* Video */}
            {hasVideo && (
              <div style={{ marginTop: '12px', marginBottom: '12px' }}>
                <h4 style={{
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: '#666',
                  marginBottom: '8px'
                }}>
                  {t('progress.video')}
                </h4>
                <a
                  href={videoLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="expanded-video-link"
                >
                  <img
                    src={videoThumbnail}
                    alt="Video thumbnail"
                    className="expanded-video-thumb"
                  />
                  <div className="expanded-video-play">
                    <div className="expanded-video-play-triangle" />
                  </div>
                </a>
              </div>
            )}

            {/* Activity metadata */}
            <div className="activity-metadata">
              <div className="metadata-item">
                <span className="metadata-label">{t('progress.dateTime')}:</span>
                <span className="metadata-value">{formatDateTime(activity.timestamp)}</span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">{t('progress.cause')}:</span>
                <span className="metadata-value">{activity.cause.title}</span>
              </div>
              {activity.cause.level && (
                <div className="metadata-item">
                  <span className="metadata-label">{t('progress.level')}:</span>
                  <span className="metadata-value">{activity.cause.level.title}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Row 5: Action buttons */}
        <div className="activity-actions">
          {(hasReflection || hasImage || hasDocument || hasDocumentLinks || hasVideo) && (
            <button
              className="expand-button"
              onClick={() => { setIsExpanded(!isExpanded); setShowFullReflection(false); }}
            >
              {isExpanded ? `▲ ${t('common.readLess')}` : `▼ ${t('common.viewDetails')}`}
            </button>
          )}
          {needsReadMore && !isExpanded && (
            <button
              className="read-more-button"
              onClick={() => setShowFullReflection(!showFullReflection)}
            >
              {showFullReflection ? t('common.readLess') : t('common.readMore')}
            </button>
          )}
          <button
            className="share-button"
            onClick={() => setShowShareModal(true)}
          >
            {t('common.share')}
          </button>
        </div>
      </div>

      {showModal && (
        <ImageModal
          imageUrl={fullSizeUrl}
          onClose={() => setShowModal(false)}
        />
      )}

      {showShareModal && (
        <ShareModal
          activity={activity}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </>
  );
});

ActivityItem.displayName = 'ActivityItem';

export default ActivityItem;
